import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INTERVALS = [
  { v: "3", label: "In 3 months" },
  { v: "6", label: "In 6 months" },
  { v: "9", label: "In 9 months" },
  { v: "12", label: "In 12 months (tenancy renewal)" },
];

export default function SeasonalReminder() {
  const [email, setEmail] = useState("");
  const [interval, setInterval] = useState("6");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { email, source: "seasonal_reminder", interval_months: parseInt(interval, 10) });
      setDone(true);
      toast.success("You're on the list — I'll be in touch when the time comes.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reminders" className="py-20 sm:py-24 border-t border-[#0B1215]/10 bg-[#E9ECEF]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="border-2 border-[#0B1215] bg-[#F8F9FA] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <div className="font-mono-label text-[#FF5A36] mb-3 flex items-center gap-2">
              <CalendarClock size={14} /> · SEASONAL CLEANS · LANDLORDS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B1215] tracking-tighter leading-[0.95]">
              Book the <span className="italic font-light">next</span> turnover,<br />
              before you forget.
            </h2>
            <p className="mt-5 text-[#495057] leading-relaxed max-w-md">
              Tenancies renew in cycles. Drop your email and pick a window — I'll get in touch
              when your next clean / turnover is due, and lock in a slot before my diary fills up.
              Friendly nudge only, no spam.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-6"
            data-testid="seasonal-reminder"
          >
            {done ? (
              <div className="bg-[#0B1215] text-[#F8F9FA] p-8" data-testid="seasonal-success">
                <div className="font-mono-label text-[#FF5A36] mb-3">· REMINDER SET</div>
                <h3 className="font-display text-2xl tracking-tight">I'll be in touch.</h3>
                <p className="text-sm text-[#F8F9FA]/70 mt-2">
                  You'll get one friendly nudge when your chosen window comes around.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="seasonal-form">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="rounded-none border-[#0B1215] h-12"
                  data-testid="seasonal-email"
                />
                <Select value={interval} onValueChange={setInterval}>
                  <SelectTrigger className="rounded-none border-[#0B1215] h-12" data-testid="seasonal-interval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVALS.map((i) => (
                      <SelectItem key={i.v} value={i.v}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0B1215] hover:bg-[#FF5A36] disabled:opacity-60 text-[#F8F9FA] px-8 py-4 font-mono-label active:scale-95 transition-all"
                  data-testid="seasonal-submit"
                >
                  {loading ? "Saving..." : "Remind me"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
